import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../utils/axiosConfig';
import '../styles/KasaYonetimi.css';

interface Kasa {
  kasaID: number;
  kasaAdi: string;
  kasaTuru: string;
  bakiye: number;
  paraBirimi: {
    paraBirimiID: number;
    birim: string;
  } | null;
}

interface KasaHareket {
  hareketID: number;
  kasaID: number;
  islemTuru: string;
  miktar: number;
  tarih: string;
  projeID?: number;
  cariID?: number;
  odemeID?: number;
  kategoriID?: number;
  aciklama?: string;
  olusturmaTarihi: string;
  kasa?: {
    kasaID: number;
    kasaAdi: string;
    kasaTuru: string;
  };
  proje?: {
    projeID: number;
    projeAdi: string;
  };
  cari?: {
    cariID: number;
    adiSoyadi: string;
  };
  kategori?: {
    kategoriID: number;
    kategoriAdi: string;
    tur: string;
  };
  ekleyen?: {
    kullaniciID: number;
    adSoyad: string;
  };
}

interface ParaBirimiToplam {
  birim: string;
  toplam: number;
}

interface DashboardData {
  nakitKasalar: Kasa[];
  bankaKasalar: Kasa[];
  toplamNakit: number;
  toplamBanka: number;
  genelToplam: number;
  nakitParaBirimleri: ParaBirimiToplam[];
  bankaParaBirimleri: ParaBirimiToplam[];
  genelParaBirimleri: ParaBirimiToplam[];
}

interface Proje {
  projeID: number;
  projeAdi: string;
}

interface Cari {
  cariID: number;
  adiSoyadi: string;
}

interface Kategori {
  kategoriID: number;
  kategoriAdi: string;
  tur: string;
}

const formatMoneyInput = (value: string): string => {
  const numbers = value.replace(/[^\d]/g, '');
  if (numbers === '') return '';
  return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const parseMoneyInput = (value: string): string => {
  return value.replace(/\./g, '');
};

const KasaYonetimi: React.FC = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'ozet' | 'nakit' | 'banka'>('ozet');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedKasa, setSelectedKasa] = useState<Kasa | null>(null);
  const [hareketler, setHareketler] = useState<KasaHareket[]>([]);

  // Banka bakiye düzenleme
  const [showBankaModal, setShowBankaModal] = useState(false);
  const [bankaBakiyeForm, setBankaBakiyeForm] = useState('');

  // Tüm nakit hareketler (işlem listesi)
  const [tumHareketler, setTumHareketler] = useState<KasaHareket[]>([]);
  const [hareketFiltre, setHareketFiltre] = useState({
    baslangic: '',
    bitis: '',
    kasaID: ''
  });

  // Sayfalama state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchDashboard();
    fetchTumHareketler();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axiosInstance.get('/api/KasaHareketleri/dashboard');
      setDashboard(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Dashboard yüklenirken hata:', error);
      toast.error('Dashboard yüklenemedi');
      setLoading(false);
    }
  };

  const fetchTumHareketler = async (filtre?: { baslangic: string; bitis: string; kasaID: string }) => {
    try {
      const f = filtre || hareketFiltre;
      const params = new URLSearchParams();
      if (f.baslangic) params.append('baslangic', f.baslangic);
      if (f.bitis) params.append('bitis', f.bitis);
      if (f.kasaID) params.append('kasaID', f.kasaID);
      const response = await axiosInstance.get(`/api/KasaHareketleri/nakit-hareketler?${params.toString()}`);
      setTumHareketler(response.data);
    } catch (error) {
      console.error('Hareketler yüklenirken hata:', error);
    }
  };

  const handleKasaClick = async (kasa: Kasa) => {
    // Banka hesapları için hareket geçmişi yok
    if (kasa.kasaTuru === 'Banka') {
      toast.info('Banka hesapları için hareket geçmişi bulunmamaktadır');
      return;
    }

    try {
      const response = await axiosInstance.get(`/api/KasaHareketleri/kasa/${kasa.kasaID}`);
      setHareketler(response.data);
      setSelectedKasa(kasa);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Hareket geçmişi yüklenirken hata:', error);
      toast.error('Hareket geçmişi yüklenemedi');
    }
  };

  const handleDeleteHareket = async (hareket: KasaHareket) => {
    let confirmMsg = 'Bu işlemi silmek istediğinizden emin misiniz?';
    if (hareket.odemeID) {
      confirmMsg = `Bu kasa hareketi bir ödeme kaydı ile ilişkilidir.\n\n` +
        `Cari: ${hareket.cari?.adiSoyadi || '—'}\n` +
        `Miktar: ${hareket.miktar.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}\n\n` +
        `Silme işlemi ile ilişkili ödeme kaydı da silinecektir. Devam etmek istiyor musunuz?`;
    }
    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      await axiosInstance.delete(`/api/KasaHareketleri/${hareket.hareketID}`);
      toast.success(hareket.odemeID ? 'İşlem ve ilişkili ödeme kaydı silindi' : 'İşlem silindi');
      if (selectedKasa) {
        handleKasaClick(selectedKasa);
      }
      fetchDashboard();
      fetchTumHareketler();
    } catch (error) {
      console.error('İşlem silinirken hata:', error);
      toast.error('İşlem silinemedi');
    }
  };

  // Banka bakiye modal aç
  const handleBankaDuzenle = (kasa: Kasa) => {
    setSelectedKasa(kasa);
    setBankaBakiyeForm(formatMoneyInput(kasa.bakiye.toString()));
    setShowBankaModal(true);
  };

  // Banka bakiye güncelleme
  const handleBankaBakiyeKaydet = async () => {
    if (!selectedKasa) return;

    if (bankaBakiyeForm === '') {
      toast.error('Bakiye değeri girin');
      return;
    }

    try {
      const response = await axiosInstance.post(`/api/Kasalar/${selectedKasa.kasaID}/bakiye-guncelle`, {
        bakiye: parseFloat(parseMoneyInput(bankaBakiyeForm))
      });
      toast.success(response.data.message || 'Bakiye güncellendi');
      setShowBankaModal(false);
      fetchDashboard();
    } catch (error: any) {
      console.error('Bakiye güncellenirken hata:', error);
      const errorMessage = error.response?.data?.message || 'Bakiye güncellenemedi';
      toast.error(errorMessage);
    }
  };

  const formatAmount = (val: number) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(val);

  // Sayfalama hesaplamaları
  const totalPages = Math.ceil(tumHareketler.length / itemsPerPage);
  const paginatedHareketler = tumHareketler.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Ozet design token helpers
  const card = {
    background: '#fff',
    borderRadius: '12px',
    border: '1px solid #f1f5f9',
    boxShadow: '0 1px 3px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04)',
  } as React.CSSProperties;

  const fmt = (n: number) =>
    new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(n);

  if (loading) {
    return (
      <>
        <div style={{ width: '100%', padding: '24px 24px', paddingBottom: '40px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 32 }}>
            <div style={{ width: 80, height: 12, background: '#f1f5f9', borderRadius: 6, marginBottom: 4 }} />
            <div style={{ width: 200, height: 24, background: '#f1f5f9', borderRadius: 8 }} />
            <div style={{ width: 280, height: 14, background: '#f8fafc', borderRadius: 6 }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
            {[1,2,3].map(i => (
              <div key={i} style={{ ...card, padding: '20px', height: 100, display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 54, height: 54, borderRadius: 14, background: '#f1f5f9', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ width: '60%', height: 26, background: '#f1f5f9', borderRadius: 6, marginBottom: 8 }} />
                  <div style={{ width: '40%', height: 10, background: '#f8fafc', borderRadius: 6 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  const statCards = [
    {
      label: 'Toplam Nakit',
      amounts: dashboard?.nakitParaBirimleri ?? [],
      iconClass: 'fas fa-money-bill-wave',
      iconBg: '#f0fdf4',
      iconColor: '#16a34a',
    },
    {
      label: 'Toplam Banka',
      amounts: dashboard?.bankaParaBirimleri ?? [],
      iconClass: 'fas fa-university',
      iconBg: '#eff6ff',
      iconColor: '#2563eb',
    },
    {
      label: 'Genel Toplam',
      amounts: dashboard?.genelParaBirimleri ?? [],
      iconClass: 'fas fa-coins',
      iconBg: '#fff8f0',
      iconColor: '#cd6906',
    },
  ];

  return (
    <>
      <div style={{ width: '100%', padding: '24px 24px', paddingBottom: '40px' }}>

        {/* ── Sayfa Başlığı ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '32px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <button
              onClick={() => navigate(-1)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '12px', fontWeight: 600, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0, transition: 'color 0.15s', letterSpacing: '0.01em', width: 'fit-content', marginBottom: 2 }}
              onMouseEnter={e => (e.currentTarget.style.color = '#0f172a')}
              onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
            >
              <i className="fas fa-arrow-left" style={{ fontSize: '10px' }}></i> Geri Dön
            </button>
            <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#0f172a', margin: 0, letterSpacing: '-0.02em', lineHeight: 1.2 }}>Kasa Yönetimi</h1>
            <p style={{ fontSize: '13.5px', color: '#64748b', margin: 0 }}>Kasalarınızı ve finansal hareketlerinizi yönetin.</p>
          </div>
          <button
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 20px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(205,105,6,0.3)', transition: 'background 0.2s', flexShrink: 0, marginTop: 22 }}
            onMouseEnter={e => (e.currentTarget.style.background = '#a85505')}
            onMouseLeave={e => (e.currentTarget.style.background = '#cd6906')}
          >
            <i className="fas fa-plus" style={{ fontSize: '11px' }}></i> Yeni Kasa Ekle
          </button>
        </div>

        {/* ── Tab Navigation ── */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: '#fff', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '5px', boxShadow: '0 1px 3px rgba(15,23,42,0.04)', width: 'fit-content' }}>
          {([
            { key: 'ozet', icon: 'fas fa-chart-pie', label: 'Özet' },
            { key: 'nakit', icon: 'fas fa-money-bill-wave', label: 'Nakit Kasalar', count: dashboard?.nakitKasalar?.length },
            { key: 'banka', icon: 'fas fa-university', label: 'Banka Hesapları', count: dashboard?.bankaKasalar?.length },
          ] as const).map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '8px 18px',
                fontSize: '13px', fontWeight: 600,
                color: activeTab === tab.key ? '#cd6906' : '#64748b',
                background: activeTab === tab.key ? '#fff8f0' : 'transparent',
                border: activeTab === tab.key ? '1px solid #f5d7b0' : '1px solid transparent',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.15s',
                whiteSpace: 'nowrap',
              }}
            >
              <i className={tab.icon} style={{ fontSize: '12px' }}></i>
              {tab.label}
              {'count' in tab && tab.count !== undefined && (
                <span style={{ fontSize: '11px', fontWeight: 600, color: activeTab === tab.key ? '#cd6906' : '#94a3b8', background: activeTab === tab.key ? '#fde8c8' : '#f1f5f9', padding: '1px 7px', borderRadius: 20 }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Özet Tab ── */}
        {activeTab === 'ozet' && (
          <>
            {/* Hero card — Toplam Nakit Mevcudiyeti */}
            <div style={{ ...card, padding: '24px 28px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20 }}>
              <div>
                <div style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Toplam Nakit Mevcudiyeti</div>
                <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'baseline' }}>
                  {(dashboard?.genelParaBirimleri ?? []).length > 0
                    ? dashboard!.genelParaBirimleri.map((pb, idx) => (
                        <React.Fragment key={pb.birim}>
                          {idx > 0 && <span style={{ fontSize: '16px', fontWeight: 400, color: '#cbd5e1', alignSelf: 'baseline', lineHeight: 1 }}>+</span>}
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                            <span style={{ fontSize: '20px', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{fmt(pb.toplam)}</span>
                            <span style={{ fontSize: '12px', fontWeight: 500, color: '#64748b' }}>{pb.birim}</span>
                          </div>
                        </React.Fragment>
                      ))
                    : <div style={{ fontSize: '20px', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.03em' }}>0,00 <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>TL</span></div>
                  }
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                {[
                  { label: 'Nakit', amounts: dashboard?.nakitParaBirimleri ?? [], bg: '#f0fdf4', border: '#bbf7d0', color: '#15803d', labelColor: '#16a34a' },
                  { label: 'Banka', amounts: dashboard?.bankaParaBirimleri ?? [], bg: '#eff6ff', border: '#bfdbfe', color: '#1d4ed8', labelColor: '#2563eb' },
                ].map(s => (
                  <div key={s.label} style={{ padding: '12px 18px', background: s.bg, border: `1px solid ${s.border}`, borderRadius: '10px', minWidth: 120 }}>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: s.labelColor, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>{s.label}</div>
                    {s.amounts.length > 0
                      ? <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                          {s.amounts.map(pb => (
                            <div key={pb.birim} style={{ fontSize: '13px', fontWeight: 700, color: s.color, fontVariantNumeric: 'tabular-nums' }}>
                              {fmt(pb.toplam)} <span style={{ fontSize: '11px', fontWeight: 500 }}>{pb.birim}</span>
                            </div>
                          ))}
                        </div>
                      : <div style={{ fontSize: '13px', fontWeight: 700, color: s.color }}>0,00 <span style={{ fontSize: '11px' }}>TL</span></div>
                    }
                  </div>
                ))}
              </div>
            </div>

            {/* 3 stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 4 }}>
              {statCards.map(sc => (
                <div
                  key={sc.label}
                  style={{ ...card, padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14, transition: 'border-color 0.18s, box-shadow 0.18s, transform 0.18s', cursor: 'default' }}
                  onMouseEnter={e => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = '#cd6906'; el.style.boxShadow = '0 4px 12px rgba(205,105,6,0.15)'; el.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = '#f1f5f9'; el.style.boxShadow = '0 1px 3px rgba(15,23,42,0.06)'; el.style.transform = 'translateY(0)'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ width: 54, height: 54, borderRadius: 14, background: sc.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className={sc.iconClass} style={{ fontSize: '22px', color: sc.iconColor }} />
                    </div>
                    <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {sc.amounts.length > 0
                        ? sc.amounts.map(pb => (
                            <div key={pb.birim} style={{ display: 'flex', alignItems: 'baseline', gap: 4, justifyContent: 'flex-end' }}>
                              <span style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{fmt(pb.toplam)}</span>
                              <span style={{ fontSize: '11px', fontWeight: 500, color: '#94a3b8' }}>{pb.birim}</span>
                            </div>
                          ))
                        : <span style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>—</span>
                      }
                    </div>
                  </div>
                  <div style={{ fontSize: '10px', fontWeight: 600, color: '#64748b', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{sc.label}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── Nakit Kasalar Tab ── */}
        {activeTab === 'nakit' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 16 }}>
            {(dashboard?.nakitKasalar ?? []).length > 0 ? dashboard!.nakitKasalar.map(kasa => (
              <div
                key={kasa.kasaID}
                style={{ ...card, padding: '20px 22px', transition: 'border-color 0.18s, box-shadow 0.18s, transform 0.18s' }}
                onMouseEnter={e => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = '#cd6906'; el.style.boxShadow = '0 4px 12px rgba(205,105,6,0.12)'; el.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = '#f1f5f9'; el.style.boxShadow = '0 1px 3px rgba(15,23,42,0.06)'; el.style.transform = 'translateY(0)'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 36, height: 36, background: '#f0fdf4', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className="fas fa-wallet" style={{ fontSize: '14px', color: '#16a34a' }} />
                    </div>
                    <span style={{ fontSize: '13.5px', fontWeight: 600, color: '#0f172a' }}>{kasa.kasaAdi}</span>
                  </div>
                  <button
                    onClick={() => handleKasaClick(kasa)}
                    style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: '8px', padding: '5px 12px', cursor: 'pointer', transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#fff8f0'; e.currentTarget.style.color = '#cd6906'; e.currentTarget.style.borderColor = '#f5d7b0'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = '#f1f5f9'; }}
                  >
                    İşlem Geçmişi
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: '18px', fontWeight: 700, color: kasa.bakiye >= 0 ? '#0f172a' : '#dc2626', letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums' }}>{fmt(kasa.bakiye)}</span>
                  <span style={{ fontSize: '12px', fontWeight: 500, color: '#94a3b8' }}>{kasa.paraBirimi?.birim ?? 'TL'}</span>
                </div>
                <div style={{ fontSize: '10px', fontWeight: 600, color: '#94a3b8', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Güncel Bakiye</div>
              </div>
            )) : (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
                <i className="fas fa-inbox" style={{ fontSize: '2rem', marginBottom: 12, display: 'block' }} />
                <p style={{ margin: 0, fontWeight: 500 }}>Nakit kasa bulunamadı</p>
              </div>
            )}
          </div>
        )}

        {/* ── Banka Hesapları Tab ── */}
        {activeTab === 'banka' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 16 }}>
            {(dashboard?.bankaKasalar ?? []).length > 0 ? dashboard!.bankaKasalar.map(kasa => (
              <div
                key={kasa.kasaID}
                style={{ ...card, padding: '20px 22px', transition: 'border-color 0.18s, box-shadow 0.18s, transform 0.18s' }}
                onMouseEnter={e => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = '#cd6906'; el.style.boxShadow = '0 4px 12px rgba(205,105,6,0.12)'; el.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = '#f1f5f9'; el.style.boxShadow = '0 1px 3px rgba(15,23,42,0.06)'; el.style.transform = 'translateY(0)'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 36, height: 36, background: '#eff6ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className="fas fa-university" style={{ fontSize: '14px', color: '#2563eb' }} />
                    </div>
                    <span style={{ fontSize: '13.5px', fontWeight: 600, color: '#0f172a' }}>{kasa.kasaAdi}</span>
                  </div>
                  <button
                    onClick={() => handleBankaDuzenle(kasa)}
                    style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: '8px', padding: '5px 12px', cursor: 'pointer', transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#fff8f0'; e.currentTarget.style.color = '#cd6906'; e.currentTarget.style.borderColor = '#f5d7b0'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = '#f1f5f9'; }}
                  >
                    Bakiye Düzenle
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: '18px', fontWeight: 700, color: kasa.bakiye >= 0 ? '#0f172a' : '#dc2626', letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums' }}>{fmt(kasa.bakiye)}</span>
                  <span style={{ fontSize: '12px', fontWeight: 500, color: '#94a3b8' }}>{kasa.paraBirimi?.birim ?? 'TL'}</span>
                </div>
                <div style={{ fontSize: '10px', fontWeight: 600, color: '#94a3b8', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Güncel Bakiye</div>
              </div>
            )) : (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
                <i className="fas fa-inbox" style={{ fontSize: '2rem', marginBottom: 12, display: 'block' }} />
                <p style={{ margin: 0, fontWeight: 500 }}>Banka hesabı bulunamadı</p>
              </div>
            )}
          </div>
        )}

        {/* ── İşlem Listesi ── */}
        <div style={{ ...card, overflow: 'hidden', marginTop: 28 }}>
          {/* Card Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 30, height: 30, borderRadius: '8px', background: '#fff8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="fas fa-list-alt" style={{ fontSize: '12px', color: '#cd6906' }} />
              </div>
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>
                İşlem Listesi
              </span>
              <span style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', background: '#f1f5f9', padding: '2px 8px', borderRadius: 20 }}>{tumHareketler.length}</span>
            </div>
          </div>

          {/* Filter Bar */}
          <div style={{ display: 'flex', gap: 12, padding: '14px 22px', background: '#fafafa', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {[
              { label: 'Başlangıç', key: 'baslangic' as const, type: 'date' as const },
              { label: 'Bitiş', key: 'bitis' as const, type: 'date' as const },
            ].map(f => (
              <div key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{f.label}</label>
                <input
                  type={f.type}
                  value={hareketFiltre[f.key]}
                  onChange={e => setHareketFiltre({ ...hareketFiltre, [f.key]: e.target.value })}
                  style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', color: '#0f172a', outline: 'none', background: '#fff', minWidth: 148, boxShadow: '0 1px 2px rgba(15,23,42,0.04)' }}
                  onFocus={e => { e.currentTarget.style.borderColor = '#cd6906'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                  onBlur={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,23,42,0.04)'; }}
                />
              </div>
            ))}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Kasa</label>
              <div style={{ position: 'relative' }}>
                <select
                  value={hareketFiltre.kasaID}
                  onChange={e => setHareketFiltre({ ...hareketFiltre, kasaID: e.target.value })}
                  style={{ padding: '7px 32px 7px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: '#0f172a', outline: 'none', background: '#fff', minWidth: 170, appearance: 'none', cursor: 'pointer', boxShadow: '0 1px 2px rgba(15,23,42,0.04)' }}
                  onFocus={e => { e.currentTarget.style.borderColor = '#cd6906'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                  onBlur={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,23,42,0.04)'; }}
                >
                  <option value="">Tümü</option>
                  {dashboard?.nakitKasalar.map(k => (
                    <option key={k.kasaID} value={k.kasaID}>{k.kasaAdi}</option>
                  ))}
                </select>
                <svg style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#94a3b8' }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>
            <button
              type="button"
              onClick={() => { fetchTumHareketler(hareketFiltre); setCurrentPage(1); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 18px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(205,105,6,0.3)', transition: 'background 0.2s' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#a85505')}
              onMouseLeave={e => (e.currentTarget.style.background = '#cd6906')}
            >
              <i className="fas fa-filter" style={{ fontSize: '11px' }}></i> Filtrele
            </button>
            <button
              type="button"
              onClick={() => { const e = { baslangic: '', bitis: '', kasaID: '' }; setHareketFiltre(e); fetchTumHareketler(e); setCurrentPage(1); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', background: '#fff', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#0f172a'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#64748b'; }}
            >
              Temizle
            </button>
          </div>

          {/* Table */}
          {tumHareketler.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                    {['Tarih', 'Kasa', 'İşlem', 'Miktar', 'Cari', 'Proje', 'Kategori', 'Açıklama'].map(col => (
                      <th key={col} style={{ padding: '11px 18px', textAlign: 'left', fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap', border: 'none' }}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedHareketler.map((hareket) => (
                    <tr
                      key={hareket.hareketID}
                      style={{ borderBottom: '1px solid #f8fafc', transition: 'background 0.12s', cursor: 'default' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#fffbf5')}
                      onMouseLeave={e => (e.currentTarget.style.background = '')}
                    >
                      <td style={{ padding: '13px 18px', whiteSpace: 'nowrap', fontSize: '13px', color: '#374151', fontWeight: 500 }}>{new Date(hareket.tarih).toLocaleDateString('tr-TR')}</td>
                      <td style={{ padding: '13px 18px', fontSize: '13px', color: '#374151' }}>{hareket.kasa?.kasaAdi || '—'}</td>
                      <td style={{ padding: '13px 18px' }}>
                        {hareket.islemTuru === 'Giris'
                          ? <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', background: '#f0fdf4', color: '#15803d', borderRadius: 20, fontSize: '11px', fontWeight: 700, border: '1px solid #bbf7d0' }}>Giriş</span>
                          : <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', background: '#fef2f2', color: '#b91c1c', borderRadius: 20, fontSize: '11px', fontWeight: 700, border: '1px solid #fecaca' }}>Çıkış</span>
                        }
                      </td>
                      <td style={{ padding: '13px 18px', fontSize: '13px', fontWeight: 700, fontFamily: "'JetBrains Mono','SF Mono','Consolas',monospace", fontVariantNumeric: 'tabular-nums', color: hareket.islemTuru === 'Giris' ? '#15803d' : '#b91c1c', whiteSpace: 'nowrap' }}>
                        {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(hareket.miktar)}
                      </td>
                      <td style={{ padding: '13px 18px', fontSize: '13px', color: '#374151' }}>{hareket.cari?.adiSoyadi || '—'}</td>
                      <td style={{ padding: '13px 18px', fontSize: '13px', color: '#374151' }}>{hareket.proje?.projeAdi || '—'}</td>
                      <td style={{ padding: '13px 18px', fontSize: '13px', color: '#374151' }}>{hareket.kategori?.kategoriAdi || '—'}</td>
                      <td style={{ padding: '13px 18px', fontSize: '13px', color: '#6b7280' }}>{hareket.aciklama || <span style={{ fontStyle: 'italic', color: '#cbd5e1' }}>—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Pagination */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderTop: '1px solid #f1f5f9', background: '#fafafa' }}>
                <span style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: 8 }}>
                  Sayfa başına:
                  <select
                    value={itemsPerPage}
                    onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    style={{ padding: '3px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: '12px', color: '#0f172a', outline: 'none', background: '#fff', cursor: 'pointer' }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </span>
                <span style={{ fontSize: '12px', color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
                  {Math.min((currentPage - 1) * itemsPerPage + 1, tumHareketler.length)}–{Math.min(currentPage * itemsPerPage, tumHareketler.length)} / {tumHareketler.length} kayıt
                </span>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
              <i className="fas fa-inbox" style={{ fontSize: '2rem', marginBottom: 12, display: 'block', opacity: 0.5 }} />
              <p style={{ margin: 0, fontWeight: 500, fontSize: '13.5px' }}>İşlem kaydı bulunamadı</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Detay Modal (İşlem Geçmişi) ── */}
      {showDetailModal && selectedKasa && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setShowDetailModal(false)}>
          <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: 860, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(15,23,42,0.2)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid #f1f5f9' }}>
              <div>
                <div style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em' }}>{selectedKasa.kasaAdi}</div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: 2 }}>İşlem Geçmişi</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <span style={{ fontSize: '15px', fontWeight: 700, color: selectedKasa.bakiye >= 0 ? '#15803d' : '#dc2626', fontVariantNumeric: 'tabular-nums' }}>
                  {fmt(selectedKasa.bakiye)} <span style={{ fontSize: '12px', fontWeight: 500, color: '#94a3b8' }}>{selectedKasa.paraBirimi?.birim}</span>
                </span>
                <button onClick={() => setShowDetailModal(false)} style={{ width: 32, height: 32, borderRadius: '8px', background: '#f8fafc', border: '1px solid #f1f5f9', cursor: 'pointer', fontSize: '18px', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>&times;</button>
              </div>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {hareketler.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#fafafa', borderBottom: '1px solid #f1f5f9', position: 'sticky', top: 0 }}>
                      {['Tarih', 'İşlem', 'Miktar', 'Cari', 'Proje', 'Kategori', 'Açıklama', ''].map((col, i) => (
                        <th key={i} style={{ padding: '10px 16px', textAlign: 'left', fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', border: 'none', whiteSpace: 'nowrap' }}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {hareketler.map(h => (
                      <tr key={h.hareketID} style={{ borderBottom: '1px solid #f8fafc', transition: 'background 0.12s' }} onMouseEnter={e => (e.currentTarget.style.background = '#fffbf5')} onMouseLeave={e => (e.currentTarget.style.background = '')}>
                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#374151', fontWeight: 500, whiteSpace: 'nowrap' }}>{new Date(h.tarih).toLocaleDateString('tr-TR')}</td>
                        <td style={{ padding: '12px 16px' }}>
                          {h.islemTuru === 'Giris'
                            ? <span style={{ display: 'inline-flex', padding: '3px 10px', background: '#f0fdf4', color: '#15803d', borderRadius: 20, fontSize: '11px', fontWeight: 700, border: '1px solid #bbf7d0' }}>Giriş</span>
                            : <span style={{ display: 'inline-flex', padding: '3px 10px', background: '#fef2f2', color: '#b91c1c', borderRadius: 20, fontSize: '11px', fontWeight: 700, border: '1px solid #fecaca' }}>Çıkış</span>
                          }
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 700, fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', color: h.islemTuru === 'Giris' ? '#15803d' : '#b91c1c', whiteSpace: 'nowrap' }}>
                          {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(h.miktar)}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#374151' }}>{h.cari?.adiSoyadi || '—'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#374151' }}>{h.proje?.projeAdi || '—'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#374151' }}>{h.kategori?.kategoriAdi || '—'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#6b7280' }}>{h.aciklama || '—'}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <button onClick={() => handleDeleteHareket(h)} style={{ width: 28, height: 28, borderRadius: '7px', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Sil">
                            <i className="fas fa-trash-alt" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
                  <i className="fas fa-inbox" style={{ fontSize: '2rem', marginBottom: 12, display: 'block', opacity: 0.5 }} />
                  <p style={{ margin: 0, fontWeight: 500, fontSize: '13.5px' }}>Henüz işlem kaydı bulunmuyor</p>
                </div>
              )}
            </div>
            <div style={{ padding: '14px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowDetailModal(false)} style={{ padding: '8px 20px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s' }}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Banka Bakiye Modal ── */}
      {showBankaModal && selectedKasa && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setShowBankaModal(false)}>
          <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(15,23,42,0.2)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid #f1f5f9' }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em' }}>Bakiye Düzenle</div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: 2 }}>{selectedKasa.kasaAdi}</div>
              </div>
              <button onClick={() => setShowBankaModal(false)} style={{ width: 32, height: 32, borderRadius: '8px', background: '#f8fafc', border: '1px solid #f1f5f9', cursor: 'pointer', fontSize: '18px', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&times;</button>
            </div>
            <div style={{ padding: '24px' }}>
              <div style={{ background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: '10px', padding: '14px 18px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Mevcut Bakiye</span>
                <span style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{fmt(selectedKasa.bakiye)} {selectedKasa.paraBirimi?.birim}</span>
              </div>
              <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Yeni Bakiye ({selectedKasa.paraBirimi?.birim}) *</label>
              <input
                type="text"
                value={bankaBakiyeForm}
                onChange={e => setBankaBakiyeForm(formatMoneyInput(e.target.value))}
                autoFocus
                style={{ width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '15px', fontWeight: 600, color: '#0f172a', outline: 'none', boxShadow: '0 1px 2px rgba(15,23,42,0.04)', fontVariantNumeric: 'tabular-nums' }}
                onFocus={e => { e.currentTarget.style.borderColor = '#cd6906'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                onBlur={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,23,42,0.04)'; }}
              />
            </div>
            <div style={{ padding: '14px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setShowBankaModal(false)} style={{ padding: '8px 20px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>İptal</button>
              <button onClick={handleBankaBakiyeKaydet} style={{ padding: '8px 20px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(205,105,6,0.3)' }}>
                <i className="fas fa-save" style={{ marginRight: 6 }}></i> Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default KasaYonetimi;
